import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { classNames } from '../../lib/utils';
import packageJson from '../../../package.json';
import { LICENSE_STATUS_CHANGED_EVENT, LicenseStatusBanner } from '../license/LicenseStatusBanner';
import { getProductModeInfo } from '../../lib/productMode';
import { STORAGE_WRITE_ERROR_EVENT } from '../../lib/storage';
import {
  listWorkspaces,
  loadCurrentActor,
  logout,
  switchActiveWorkspace,
  type AuthActor,
  type AuthWorkspace,
  webAuthEnabled,
} from '../../lib/authClient';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  matchPaths?: string[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const dashboardItem: NavItem = {
  path: '/',
  label: 'ダッシュボード',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
};

const navGroups: NavGroup[] = [
  {
    label: 'ホーム',
    items: [dashboardItem],
  },
  {
    label: '取引入力',
    items: [
      {
        path: '/incomes',
        label: '収入',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      {
        path: '/expenses',
        label: '経費',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
          </svg>
        ),
      },
    ],
  },
  {
    label: '管理設定',
    items: [
      {
        path: '/projects',
        label: 'プロジェクト',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        ),
      },
      {
        path: '/parties',
        label: '相手先',
        matchPaths: ['/members', '/counterparties'],
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
      },
      {
        path: '/rules',
        label: '分配ルール',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 12h6m-6 4h6" />
          </svg>
        ),
      },
      {
        path: '/rules/graph-preview',
        label: 'ノードプレビュー',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M6 8a2 2 0 100-4 2 2 0 000 4zm0 0v8m0 0a2 2 0 100 4 2 2 0 000-4zm0 0h8m4-8a2 2 0 100-4 2 2 0 000 4zm0 0v8m0 0a2 2 0 100 4 2 2 0 000-4zm0-8H6" />
          </svg>
        ),
      },
    ],
  },
  {
    label: '確認・出力',
    items: [
      {
        path: '/reports',
        label: 'レポート',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      },
      {
        path: '/payment-report',
        label: '報告書確認',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
      },
      {
        path: '/closing',
        label: '確定処理',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'システム',
    items: [
      {
        path: '/settings',
        label: '設定',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
      },
      {
        path: '/settings/accounting',
        label: '会計連携',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 14h6m-6 4h6M8 6h8m-9 14h10a2 2 0 002-2V6.8a2 2 0 00-.586-1.414l-2.8-2.8A2 2 0 0014.2 2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        path: '/settings/report-issuer',
        label: '帳票発行者',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.5L19 9.5V19a2 2 0 01-2 2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M14 3v5a2 2 0 002 2h3" />
          </svg>
        ),
      },
      {
        path: '/settings/environment',
        label: '環境設定',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 6V4m0 16v-2m6-6h2M4 12h2m10.243-4.243l1.414-1.414M6.343 17.657l1.414-1.414m8.486 0l1.414 1.414M6.343 6.343l1.414 1.414M9 12a3 3 0 106 0 3 3 0 00-6 0z" />
          </svg>
        ),
      },
      {
        path: '/help',
        label: 'ヘルプ',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 6.75a3 3 0 00-3 3m3-3a3 3 0 013 3c0 1.5-.9 2.15-1.83 2.82-.72.52-1.17.9-1.17 1.68M12 18h.01M4.5 5.75A2.25 2.25 0 016.75 3.5h10.5a2.25 2.25 0 012.25 2.25v12.5a2.25 2.25 0 01-2.25 2.25H6.75a2.25 2.25 0 01-2.25-2.25V5.75z" />
          </svg>
        ),
      },
    ],
  },
];

const navItems = navGroups.flatMap(group => group.items);

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [productInfo, setProductInfo] = useState(() => getProductModeInfo());
  const [storageError, setStorageError] = useState('');
  const [authError, setAuthError] = useState('');
  const [actor, setActor] = useState<AuthActor | null>(null);
  const [workspaces, setWorkspaces] = useState<AuthWorkspace[]>([]);
  const [switchingWorkspace, setSwitchingWorkspace] = useState(false);
  const location = useLocation();
  const licenseMismatchBlocked = productInfo.licenseState === 'device-mismatch' && location.pathname !== '/settings/environment';

  useEffect(() => {
    const refresh = () => setProductInfo(getProductModeInfo());
    const onStorageWriteError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      setStorageError(detail?.message ?? 'SQLite保存に失敗しました');
    };
    window.addEventListener(LICENSE_STATUS_CHANGED_EVENT, refresh);
    window.addEventListener(STORAGE_WRITE_ERROR_EVENT, onStorageWriteError);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(LICENSE_STATUS_CHANGED_EVENT, refresh);
      window.removeEventListener(STORAGE_WRITE_ERROR_EVENT, onStorageWriteError);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  useEffect(() => {
    if (!webAuthEnabled()) return;
    let cancelled = false;
    Promise.all([loadCurrentActor(), listWorkspaces()])
      .then(([currentActor, workspaceResult]) => {
        if (cancelled) return;
        setActor(currentActor);
        setWorkspaces(workspaceResult.workspaces);
      })
      .catch(() => {
        if (!cancelled) {
          setActor(null);
          setWorkspaces([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    setAuthError('');
    try {
      await logout();
      window.location.assign('/login');
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'ログアウトに失敗しました');
    }
  };

  const handleWorkspaceChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const workspaceId = event.target.value;
    if (!workspaceId || workspaceId === actor?.workspace?.id) return;
    setAuthError('');
    setSwitchingWorkspace(true);
    try {
      setActor(await switchActiveWorkspace(workspaceId));
      window.location.reload();
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'ワークスペースの切替に失敗しました');
      setSwitchingWorkspace(false);
    }
  };

  // Get current page title
  const currentNav = navItems.find(item => item.path === location.pathname) ?? navItems.find(item =>
    item.path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(item.path)
  ) ?? (location.pathname.startsWith('/rules') ? {
    label: 'プロジェクト別 分配ルール設定',
  } : undefined);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={classNames(
          'fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200',
          'flex flex-col transform transition-transform duration-200',
          'lg:relative lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-200">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">収益分配</p>
            <p className="text-xs text-gray-500">管理システム</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {navGroups.map(group => (
            <div key={group.label} className="space-y-1">
              <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {group.label}
              </p>
              {group.items.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/' || item.path === '/settings'}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => {
                    const matched = isActive || (item.matchPaths ?? []).some(path => location.pathname.startsWith(path));
                    return classNames(
                      'flex items-center gap-3 min-h-10 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                      matched
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    );
                  }}
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200">
          <p className="text-xs text-gray-400 text-center">v{packageJson.version}</p>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
            aria-label="メニューを開く"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">
            {currentNav?.label ?? 'ページ'}
          </h1>
          {webAuthEnabled() && (
            <div className="ml-auto flex min-w-0 items-center gap-3">
              <div className="hidden min-w-0 text-right sm:block">
                <p className="truncate text-sm font-medium text-gray-900">{actor?.user.email ?? 'ログイン中'}</p>
                {actor?.workspace && workspaces.length <= 1 && (
                  <p className="truncate text-xs text-gray-500">{actor.workspace.name}</p>
                )}
              </div>
              {workspaces.length > 1 && (
                <select
                  value={actor?.workspace?.id ?? ''}
                  onChange={handleWorkspaceChange}
                  disabled={switchingWorkspace}
                  className="hidden max-w-56 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 md:block"
                  aria-label="ワークスペースを切り替える"
                >
                  {workspaces.map(workspace => (
                    <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                ログアウト
              </button>
            </div>
          )}
        </header>
        <LicenseStatusBanner />
        {authError && (
          <div className="border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <div className="flex items-start justify-between gap-4">
              <span>{authError}</span>
              <button
                type="button"
                className="shrink-0 rounded px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
                onClick={() => setAuthError('')}
              >
                閉じる
              </button>
            </div>
          </div>
        )}
        {storageError && (
          <div className="border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <div className="flex items-start justify-between gap-4">
              <span>{storageError}</span>
              <button
                type="button"
                className="shrink-0 rounded px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
                onClick={() => setStorageError('')}
              >
                閉じる
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {licenseMismatchBlocked ? (
            <div className="mx-auto max-w-2xl rounded-lg border border-red-200 bg-white p-6 shadow-sm">
              <p className="text-sm font-semibold text-red-700">ライセンス違反を検知しました</p>
              <h2 className="mt-2 text-xl font-bold text-gray-900">この端末では起動できません</h2>
              <p className="mt-3 text-sm leading-6 text-gray-600">
                読み込まれているライセンスファイルは別のPC向けに発行されています。
                この端末で利用する場合は、環境設定で認証を解除し、この端末の認証リクエストファイルから発行したライセンスを読み込んでください。
              </p>
            </div>
          ) : children}
        </main>
      </div>
    </div>
  );
}
